// perfMarks — named `performance.mark`/`measure` spans for the report runtime (ORP-22839).
//
// Why a second file: `perfLogger.ts` holds the console `%c` styling and is the ONE path
// `scripts/check-design-token-ownership.mjs` exempts from the raw-colour gate (an exact-path Set).
// This module therefore stays strictly colour-free and console-free — it only writes User Timing
// entries and hands the phase numbers to `perfLogger` to render.
//
// Everything here is gated on `perfLogger.isEnabled()`, which is one cached boolean read. With the
// switch off nothing is marked, nothing is measured and no run state is allocated, so the call
// sites below are free and change no behaviour.
//
// The spans form one run per report load:
//
//   orphex:report:<label>:start
//   orphex:http:<METHOD> <url>:start … :end          (one pair per controller request)
//   orphex:report:<label>:transform:start … :end     → measure orphex:report:<label>:transform
//   orphex:report:<label>:first-row                  → measure orphex:report:<label>:to-first-row
//   orphex:report:<label>:chart                      → measure orphex:report:<label>:to-chart
//   orphex:report:<label>:end                        → measure orphex:report:<label>:total
//
// Read them back in devtools with `performance.getEntriesByType('measure')`, or let
// `endReportRun()` print the grouped table.

import { perfLogger } from './perfLogger'

const REPORT = 'orphex:report'
const HTTP = 'orphex:http'

interface RunState {
  label: string
  startedAt: number
  transformStartedAt: number | null
  transformMs: number | null
  firstRowMs: number | null
  chartMs: number | null
}

let run: RunState | null = null
let flushHandle: ReturnType<typeof setTimeout> | null = null

/**
 * A report page has NO single orchestrator: `ReportPage` renders N sections, each with its own
 * `useReportData` watcher, so nothing knows when the last one has rendered. The run therefore
 * closes on a deferred flush that every render mark re-arms — the run ends one macrotask after the
 * last table or chart mount, which is exactly "the page stopped rendering".
 */
function armFlush(): void {
  if (flushHandle !== null) clearTimeout(flushHandle)
  flushHandle = setTimeout(() => {
    flushHandle = null
    endReportRun()
  }, 0)
}

function cancelFlush(): void {
  if (flushHandle === null) return
  clearTimeout(flushHandle)
  flushHandle = null
}

function mark(name: string): void {
  performance.mark(name)
}

function measureBetween(name: string, start: string, end: string): void {
  try {
    performance.measure(name, start, end)
  } catch {
    // A missing start mark (run reset mid-flight, or a stray end) must never break a render.
  }
}

/**
 * Open a run. `label` identifies the report — use a stable, human-readable id (the section id or
 * the layout name), because it becomes the mark prefix the team greps for.
 *
 * An already-open run is flushed first, so a run can never be silently lost by a second load.
 */
export function startReportRun(label: string): void {
  if (!perfLogger.isEnabled()) return
  if (run) endReportRun()

  run = {
    label,
    startedAt: performance.now(),
    transformStartedAt: null,
    transformMs: null,
    firstRowMs: null,
    chartMs: null,
  }
  mark(`${REPORT}:${label}:start`)
  perfLogger.startSession(label)
}

/** Transform start — call immediately before the raw controller payload is reshaped. */
export function markTransformStart(): void {
  if (!run) return
  run.transformStartedAt = performance.now()
  mark(`${REPORT}:${run.label}:transform:start`)
}

/** Transform end — the reshape is done and rows/series exist. Accumulates across sections. */
export function markTransformEnd(): void {
  if (!run || run.transformStartedAt === null) return
  const elapsed = performance.now() - run.transformStartedAt
  run.transformMs = (run.transformMs ?? 0) + elapsed
  run.transformStartedAt = null

  const { label } = run
  mark(`${REPORT}:${label}:transform:end`)
  measureBetween(
    `${REPORT}:${label}:transform`,
    `${REPORT}:${label}:transform:start`,
    `${REPORT}:${label}:transform:end`,
  )
}

/** The table is mounted AND has rows. First occurrence wins — this is time-to-first-row. */
export function markFirstRow(): void {
  if (!run || run.firstRowMs !== null) return
  run.firstRowMs = performance.now() - run.startedAt

  const { label } = run
  mark(`${REPORT}:${label}:first-row`)
  measureBetween(
    `${REPORT}:${label}:to-first-row`,
    `${REPORT}:${label}:start`,
    `${REPORT}:${label}:first-row`,
  )
  armFlush()
}

/** The chart is mounted. First occurrence wins — this is time-to-chart. */
export function markChart(): void {
  if (!run || run.chartMs !== null) return
  run.chartMs = performance.now() - run.startedAt

  const { label } = run
  mark(`${REPORT}:${label}:chart`)
  measureBetween(
    `${REPORT}:${label}:to-chart`,
    `${REPORT}:${label}:start`,
    `${REPORT}:${label}:chart`,
  )
  armFlush()
}

/** Close the run: emit the total measure and print the grouped breakdown. Idempotent. */
export function endReportRun(): void {
  cancelFlush()
  if (!run) return
  const { label, startedAt, transformMs, firstRowMs, chartMs } = run
  run = null

  mark(`${REPORT}:${label}:end`)
  measureBetween(`${REPORT}:${label}:total`, `${REPORT}:${label}:start`, `${REPORT}:${label}:end`)

  perfLogger.endReportSession({
    transformMs,
    firstRowMs,
    chartMs,
    totalMs: performance.now() - startedAt,
  })
}

/**
 * One settled HTTP span, from the `shared/http` interceptor's existing start stamp. Emitted for
 * every request while a run is open OR not — a request outside a run still gets its User Timing
 * entry, which is what makes the fan-out visible before the page has rendered anything.
 */
export function markHttpSpan(
  method: string,
  url: string,
  startedAt: number,
  endedAt: number,
): void {
  if (!perfLogger.isEnabled()) return
  const name = `${HTTP}:${method.toUpperCase()} ${url}`
  try {
    performance.measure(name, { start: startedAt, end: endedAt })
  } catch {
    // Older engines reject the options form; the duration is still on the perfLogger request line.
  }
}

/** Test seam / per-navigation drop: abandon an open run without printing it. */
export function resetReportRun(): void {
  cancelFlush()
  run = null
}

/** Test seam: is a run currently open? */
export function hasOpenReportRun(): boolean {
  return run !== null
}
